import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { pushToMatomoIfPossible } from "@utdanningsdirektoratet/utils/analytics";
import Cookies from "js-cookie";
import { Button } from "@utdanningsdirektoratet/button";
import { Toggle } from "@utdanningsdirektoratet/toggle";
import { useHflPostHog } from "@utdanningsdirektoratet/hflposthog";
import { ButtonLink } from "@utdanningsdirektoratet/link";
import { Icon } from "@utdanningsdirektoratet/icon";
import AnimateHeight from "react-animate-height";
import styles from "./CookieBanner.module.less";
import { Cookie, NecessaryCookie, StatisticsCookie } from "./utils";

type CookieBannerProps = {
  text?: string;
  title?: string;
  ariaLabel?: string;
  necessaryCookie: Cookie;
  cookies?: Array<Cookie>;
  className?: string;
  cookieDomain?: string;
  cookieDuration?: number;
  reloadOnAccept?: boolean;
  show?: boolean;
  onAccept?: (acceptedCookies: Array<string>) => void;
  defaultTexts?: {
    acceptChosen: string;
    acceptAll: string;
    acceptNecessary: string;
    showCookies: string;
    hideCookies: string;
  };
};

const _defaultTexts: CookieBannerProps["defaultTexts"] = {
  acceptAll: "Godta alle",
  acceptChosen: "Godta valgte",
  acceptNecessary: "Godta kun nødvendige",
  showCookies: "Vis hvilke informasjonskapsler vi bruker",
  hideCookies: "Skjul hvilke informasjonskapsler vi bruker",
};

const purgeCookie = (name: string, cookieDomain?: string) => {
  Cookies.remove(name, { domain: cookieDomain });
  Cookies.remove(name, { domain: cookieDomain, secure: true });
  // Delete remnants of old cookies set with previous config
  Cookies.remove(name, {
    domain: window.location.hostname.split(".").slice(-2).join("."),
  });
  Cookies.remove(name, {
    domain: window.location.hostname.split(".").slice(-2).join("."),
    secure: true,
  });
};

const CookieBanner: React.FC<CookieBannerProps> = ({
  show,
  title = "Om informasjonskapsler",
  text = "Vi bruker informasjonskapsler (cookies) på nettsidene våre og du kan velge hvilke typer informasjonskapsler vi samler inn fra deg.",
  ariaLabel = "Samtykke for bruk av informasjonskapsler",
  necessaryCookie = NecessaryCookie([]),
  cookies = [],
  onAccept,
  className,
  cookieDuration = 90,
  cookieDomain,
  reloadOnAccept = false,
  defaultTexts = _defaultTexts,
}) => {
  const hflPostHog = useHflPostHog();
  const isVisible = show || Cookies.get(necessaryCookie.key) !== "true";
  const [isClosing, setClosing] = React.useState(false);
  const [acceptedCookies, setAcceptedCookies] = React.useState<{
    [key: Cookie["key"]]: boolean;
  }>(
    cookies.reduce(
      (acc, cookie) => ({
        ...acc,
        [cookie.key]: Cookies.get(cookie.key)
          ? Cookies.get(cookie.key) === "true"
          : true,
      }),
      {},
    ),
  );
  const [showCookies, setShowCookies] = React.useState(false);

  const optInAnalytics = React.useCallback(() => {
    pushToMatomoIfPossible(["rememberConsentGiven"]);
    if (
      !hflPostHog.has_opted_in_capturing() ||
      hflPostHog.has_opted_out_capturing()
    ) {
      hflPostHog.opt_in_capturing();
    }
  }, [hflPostHog]);

  const optOutAnalytics = React.useCallback(() => {
    pushToMatomoIfPossible(["forgetConsentGiven"]);
    hflPostHog.opt_out_capturing();
  }, [hflPostHog]);

  React.useEffect(() => {
    pushToMatomoIfPossible(["requireConsent"]);
  }, []);

  React.useEffect(() => {
    if (Cookies.get(StatisticsCookie([]).key) === "true") {
      optInAnalytics();
    } else {
      optOutAnalytics();
    }
  }, [optInAnalytics, optOutAnalytics]);

  const actionBarClass = cls(
    {
      [styles.cookieBanner]: true,
      [styles.cookieBannerVisible]: isVisible || isClosing,
      [styles.cookieBannerShow]: isVisible,
      [styles.cookieBannerClose]: isClosing,
    },
    className,
  );

  const onAnimationEnd = () => {
    if (isClosing) {
      if (onAccept)
        onAccept([
          necessaryCookie.key,
          ...Object.keys(acceptedCookies).filter(
            (key) => acceptedCookies[key] === true,
          ),
        ]);
      if (reloadOnAccept) window.location.reload();
      setClosing(false);
    }
  };

  const acceptAll = () => {
    purgeCookie(necessaryCookie.key, cookieDomain);
    Cookies.set(necessaryCookie.key, "true", {
      expires: cookieDuration,
      domain: cookieDomain,
      secure: true,
    });
    cookies.forEach(({ key, domain }) => {
      purgeCookie(key, domain ?? cookieDomain);
      Cookies.set(key, "true", {
        expires: cookieDuration || undefined,
        domain: domain ?? cookieDomain,
        secure: true,
      });
    });
    setAcceptedCookies(
      cookies.reduce((acc, cookie) => ({ ...acc, [cookie.key]: true }), {}),
    );
    optInAnalytics();
    setClosing(true);
  };

  const accept = (onlyNecessary = false) => {
    purgeCookie(necessaryCookie.key, cookieDomain);
    Cookies.set(necessaryCookie.key, "true", {
      expires: cookieDuration || undefined,
      domain: cookieDomain,
      secure: true,
    });
    if (onlyNecessary) {
      setAcceptedCookies(
        cookies.reduce((acc, cookie) => ({ ...acc, [cookie.key]: false }), {}),
      );
      optOutAnalytics();
    } else {
      Object.keys(acceptedCookies)
        .filter((key) => acceptedCookies[key] === true)
        .forEach((key) => {
          purgeCookie(
            key,
            cookies.find((c) => c.key === key)?.domain ?? cookieDomain,
          );
          Cookies.set(key, "true", {
            expires: cookieDuration || undefined,
            domain: cookies.find((c) => c.key === key)?.domain ?? cookieDomain,
            secure: true,
          });
        });

      if (
        Object.keys(acceptedCookies).some(
          (key) =>
            acceptedCookies[key] === true && key === StatisticsCookie([]).key,
        )
      ) {
        optInAnalytics();
      } else {
        optOutAnalytics();
      }
    }
    cookies
      .filter((cookie) => onlyNecessary || acceptedCookies[cookie.key] !== true)
      .forEach(({ key, domain }) => {
        purgeCookie(key, domain ?? cookieDomain);
        Cookies.set(key, "false", {
          expires: cookieDuration || undefined,
          domain: domain ?? cookieDomain,
          secure: true,
        });
      });

    setClosing(true);
  };

  const hasCustomSettings = Object.values(acceptedCookies).some(
    (value) => !value,
  );

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className={actionBarClass}
      onAnimationEnd={onAnimationEnd}
    >
      <div className={styles.cookieBannerHeader}>
        <div className={styles.cookieBannerDescription}>
          <div className={styles.cookieBannerBold}>{title}</div>
          <p>{text}</p>
        </div>
        <div className={styles.cookieBannerToggles}>
          <div key={necessaryCookie.key} className={styles.cookieBannerToggle}>
            {necessaryCookie.title}
            <Toggle
              label={necessaryCookie.title}
              size="small"
              onChange={() => null}
              value
              disabled
            />
          </div>
          {cookies.map(({ key, title: cookieTitle }) => (
            <div key={key} className={styles.cookieBannerToggle}>
              {cookieTitle}
              <Toggle
                label={cookieTitle}
                size="small"
                onChange={(value) =>
                  setAcceptedCookies((prev) => ({ ...prev, [key]: value }))
                }
                value={acceptedCookies[key]}
              />
            </div>
          ))}
        </div>
        <div className={styles.cookieBannerButtons}>
          <Button onClick={() => accept(true)}>
            {defaultTexts.acceptNecessary}
          </Button>
          <Button
            primary
            onClick={() => (hasCustomSettings ? accept() : acceptAll())}
          >
            {hasCustomSettings
              ? defaultTexts.acceptChosen
              : defaultTexts.acceptAll}
          </Button>
        </div>
      </div>
      <div className={styles.cookieBannerContent}>
        <ButtonLink onClick={() => setShowCookies(!showCookies)}>
          {showCookies ? _defaultTexts.hideCookies : _defaultTexts.showCookies}
          <Icon
            icon={showCookies ? "angle up" : "angle down"}
            className={styles.cookieBannerArrowIcon}
          />
        </ButtonLink>
        <AnimateHeight height={showCookies ? "auto" : 0}>
          <div className={styles.cookieBannerCookiesContainer}>
            {[necessaryCookie, ...cookies].map((cookie) => (
              <div
                key={cookie.key}
                className={styles.cookieBannerCookieContainer}
              >
                <div className={styles.cookieBannerCookieHeader}>
                  {cookie.title}
                </div>
                <p>{cookie.description}</p>
                {cookie.vendors.map((vendor) => (
                  <div key={vendor.name}>
                    <div className={styles.cookieBannerVendorHeader}>
                      {vendor.name}
                    </div>
                    <p>
                      <span className={styles.cookieBannerBold}>Formål: </span>
                      {vendor.description}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </AnimateHeight>
      </div>
    </div>
  );
};

export default CookieBanner;
